import React from "react";
import { t } from "ttag";

import { GridItem, SortType } from "../interfaces";
import { Grid } from "./Grid";
import { SearchField } from "./SearchField";
import { SortDropdown } from "./SortDropdown";

const fetchGridData = async (
    baseApiUrl: string,
    sortType: SortType,
    filterBy?: number | null,
    searchString?: string,
): Promise<{ results: GridItem[]; next: string | null }> => {
    const apiURL = new URL(baseApiUrl);
    apiURL.searchParams.set("format", "json");
    apiURL.searchParams.set("ordering", sortType);

    if (filterBy) {
        apiURL.searchParams.set("category", `${filterBy}`);
    }
    if (searchString) {
        apiURL.searchParams.set("search", searchString);
    }

    const response = await fetch(apiURL.toString());
    return response.json();
};

const fetchMoreItems = async (
    nextUrl: string,
): Promise<{ results: GridItem[]; next: string | null }> => {
    const response = await fetch(nextUrl);
    return response.json();
};

export const TempurLoveGrid = (props: {
    filterBy?: number | null;
    baseApiUrl: string;
}): React.ReactNode => {
    const [gridData, setGridData] = React.useState<GridItem[]>([]);
    const [sortType, setSortType] = React.useState<SortType>("-date");
    const [nextUrl, setNextUrl] = React.useState<string | null>(null);
    const [appendItems, setAppendItems] = React.useState(false);

    const updateGrid = React.useCallback(
        async (searchString?: string): Promise<void> => {
            const data = await fetchGridData(
                props.baseApiUrl,
                sortType,
                props.filterBy,
                searchString,
            );
            setGridData(data.results);
            setNextUrl(data.next);
            setAppendItems(false);
        },
        [props.baseApiUrl, props.filterBy, sortType],
    );
    const updateGridRef = React.useRef(updateGrid);

    React.useEffect(() => {
        updateGridRef.current = updateGrid;
    }, [updateGrid]);

    const loadMore = async (): Promise<void> => {
        // The button that calls this should never be shown unless this exists, so this if statement is here mostly to satisfy typescript
        if (nextUrl) {
            const data = await fetchMoreItems(nextUrl);
            setGridData((prevGridData) => [...prevGridData, ...data.results]);
            setNextUrl(data.next);
            setAppendItems(true);
        }
    };

    const handleSortChange = (
        e: React.ChangeEvent<HTMLSelectElement>,
    ): void => {
        setSortType(e.target.value as SortType);
    };

    React.useEffect(() => {
        updateGridRef.current();
    }, [sortType]);

    return (
        <>
            <div className="l-full-width">
                <div className="l-capped-width">
                    <article className="search">
                        <SearchField onSearch={updateGrid} />
                        <SortDropdown
                            onChange={handleSortChange}
                            value={sortType}
                        />
                    </article>
                </div>
            </div>
            {gridData.length > 0 && (
                <Grid data={gridData} appendItems={appendItems} />
            )}
            {nextUrl && (
                <button
                    className="button button--full-width button--alabaster load-more-posts al-tempur-love--load-more-posts"
                    onClick={loadMore}
                >
                    {t`Load More Posts`}
                </button>
            )}
        </>
    );
};
